import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Pencil } from "lucide-react";

import { Button, useToast } from "../../../components";
import FormHeader from "../../../components/form/FormHeader";
import MerchantForm from "../components/merchant-form/MerchantForm";
import {
  createDefaultMerchantEditorValues,
  updateMerchantEditorSchema,
  type MerchantEditorFormValues,
} from "../components/merchant-form/merchant.scheme";
import { useGetDetailMerchant, useUpdateMerchant } from "../hooks";
import type { UpdateMerchantAdminRequest } from "../types/merchant.types";
import { mapMerchantToFormValues } from "../utils/merchant.utils";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { goBackOrGoHome } from "../../../utils/navigation";
import LeftArrow from "../../../assets/icons/left-arrow-gray-small.svg";

type MerchantUpdatePageProps = {
  basePath?: string;
};

export default function MerchantUpdatePage({
  basePath = "/merchant-management",
}: MerchantUpdatePageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { updateMerchant, loading: isSubmitting } = useUpdateMerchant();
  const { addToast } = useToast();
  const { merchant, loading: isLoadingMerchant, error: merchantError } = useGetDetailMerchant(id);
  const [values, setValues] = useState<MerchantEditorFormValues>(
    createDefaultMerchantEditorValues(),
  );
  const [formError, setFormError] = useState<string | null>(null);
  const formId = "merchant-update-form";

  useEffect(() => {
    if (merchant) {
      setValues(mapMerchantToFormValues(merchant));
    }
  }, [merchant]);

  const handleUpdateMerchant = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!merchant || !id) return;

    const parsed = updateMerchantEditorSchema.safeParse(values);
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? t("merchant.update_error"));
      return;
    }
    setFormError(null);

    const submitRequest: UpdateMerchantAdminRequest = {
      business_name: parsed.data.business_name || undefined,
      short_intro: parsed.data.short_intro || undefined,
      description: parsed.data.description || undefined,
      business_url: parsed.data.business_url || undefined,
      business_category_id: parsed.data.business_category_id || undefined,
      partner_type: parsed.data.partner_type || undefined,
      phone: parsed.data.phone || undefined,
    };

    try {
      await updateMerchant(merchant.id, submitRequest);
      addToast({
        type: "success",
        title: "Success!",
        message: t("merchant.update_success"),
      });
      navigate(`${basePath}/view/${merchant.id}`);
    } catch (updateError: unknown) {
      const message = getApiErrorMessage(updateError, t("merchant.update_error"));
      setFormError(message);
      addToast({
        type: "error",
        title: "Error!",
        message,
      });
    }
  };

  if (isLoadingMerchant || !merchant) {
    if (merchantError && !merchant && !isLoadingMerchant) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-white dark:bg-gray-900">
          <div className="text-center">
            <p className="mb-4 text-sm text-red-600 dark:text-red-400">{merchantError}</p>
            <Button
              onClick={() => navigate(basePath)}
              type="button"
            >
              {t("common.back")}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
          <p className="text-sm text-gray-600 dark:text-gray-300">{t("merchant.loading_detail")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900">
      <FormHeader
        onBack={() => goBackOrGoHome(navigate)}
        title={t("merchant.edit_merchant")}
        titleIcon={
          <Pencil
            className="h-5 w-5"
            aria-hidden="true"
          />
        }
        subtitle=""
        backLabel={t("common.back")}
        backIcon={
          <img
            src={LeftArrow}
            alt=""
            className="dark:invert"
          />
        }
        actions={
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-10 rounded-md"
              onClick={() => navigate(`${basePath}/view/${merchant.id}`)}
              disabled={isSubmitting}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              form={formId}
              variant="default"
              className="rounded-md bg-emerald-500 text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? t("merchant.updating") : t("common.update")}
            </Button>
          </div>
        }
      />
      <form
        id={formId}
        onSubmit={handleUpdateMerchant}
        className="mx-auto max-w-5xl px-4 py-6 sm:px-6"
      >
        <MerchantForm
          values={values}
          onChange={setValues}
          disabled={isSubmitting}
        />
        {formError ? (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400">{formError}</p>
        ) : null}
      </form>
    </div>
  );
}
